<script setup lang="ts">
/**
 * 交付物清单（0904 Tony：「整个交付物模块的操作很不直观和便捷，节点要一个个找」）。
 *
 * 原来的页面是一张按到期日平铺的清单：一个项目 120 条，找自己那个节点要从头翻到尾；
 * 传文件、提交审批、审批通过全都藏在「编辑」弹窗的最底下——
 * 生产库 3000 条交付物里 2508 条一步都没动过，这就是结果。
 *
 * 这里按三件事重排：
 *   1. **按阶段分组、按计划顺序排**：交付物几乎一条对一个计划节点，清单就该长得像计划表。
 *   2. **常用动作放在行上**：传文件 / 提交审批 / 通过·退回 / 下载，一键做完；
 *      「详情」才进那个大弹窗（改字段、看审批链、看历史）。
 *   3. **先筛出「和我有关的」**：我负责的、轮到我审的、缺文件的、已逾期的，一点就到。
 *
 * 批量：勾几条一起提交审批、一起设责任人 / 完成日期——项目经理开局要给 120 条配人，
 * 逐条点开弹窗改是劝退的第一步。
 */
import { computed, ref, watch } from 'vue';
import { eqsApi } from '@/api/eqs';
import ApprovalDecisionDialog from '@/components/ApprovalDecisionDialog.vue';
import PersonPicker from '@/components/PersonPicker.vue';
import StatusChip from '@/components/StatusChip.vue';
import { useWorkCalendar } from '@/composables/useWorkCalendar';
import { notify } from '@/services/notice';
import type { ModuleRecord } from '@/types/domain';
import { errorMessage, formatCell, formatResponsibility } from '@/utils/format';
import { personLabel, type PickablePerson } from '@/utils/person';
import { deliverableDueAt } from '@/utils/project-workflow';

const props = withDefaults(defineProps<{
  projectId: string | number;
  /** 本页的交付物（已经过看板下钻筛选）；顺序由后端按计划排好 */
  items: ModuleRecord[];
  people: PickablePerson[];
  writable: boolean;
  /** 当前登录人；「我负责的」按它判 */
  viewer?: { id: number | null; name: string } | null;
  /** 弹窗里正打开的那条，行上亮一下 */
  selectedId?: string | number | null;
  /** 从首页卡片带过来的预选筛选（?quick=mine / decide / no-file …）；认不出就当没传 */
  initialQuick?: string | null;
  /** Gate 提醒带过来的阶段（?phase=P05）；认不出就当没传 */
  initialPhase?: string | null;
}>(), {
  viewer: null,
  selectedId: null,
  initialQuick: null,
  initialPhase: null,
});

const emit = defineEmits<{
  /** 打开详情弹窗（字段编辑 / 审批链 / 历史） */
  open: [item: ModuleRecord];
  /** 行上或批量做了写入，外层重新拉一遍 */
  changed: [];
}>();

type QuickKey = 'all' | 'mine' | 'decide' | 'no-file' | 'ready' | 'in-review' | 'rejected' | 'overdue' | 'done';

interface RowState {
  hasFile: boolean;
  done: boolean;
  inReview: boolean;
  rejected: boolean;
  cancelled: boolean;
  overdue: boolean;
  applicable: boolean;
  mine: boolean;
  canDecide: boolean;
  canSubmit: boolean;
  /** 这条不用审批（节点既没审批流也没评审角色），给「标记完成」而不是「提交审批」 */
  canComplete: boolean;
  chipValue: string;
  chipLabel: string;
}

const DONE_STATUSES = new Set(['approved', 'completed', 'archived']);
const SUBMITTABLE_STATUSES = new Set(['draft', 'pending', 'rejected']);

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function idOf(item: ModuleRecord): string {
  return String(item.id);
}

/** 一行的所有判断只算一次，清单、筛选、批量都读这一份，口径不会分叉 */
function stateOf(item: ModuleRecord): RowState {
  const status = text(item.status);
  const approval = text(item.approvalStatus);
  const hasFile = item.documentId !== null && item.documentId !== undefined && item.documentId !== '';
  const done = DONE_STATUSES.has(status);
  const cancelled = status === 'cancelled';
  const inReview = approval === 'pending' && !done && !cancelled;
  const rejected = !done && !inReview && (status === 'rejected' || approval === 'rejected');
  const dueAt = text(item.dueAt);
  const dueTime = dueAt ? new Date(dueAt).getTime() : Number.NaN;
  const overdue = !done && !cancelled && Number.isFinite(dueTime) && dueTime < Date.now();
  const applicable = item.nodeApplicable !== false;
  const ownerId = item.ownerUserId === null || item.ownerUserId === undefined ? null : Number(item.ownerUserId);
  const ownerName = formatResponsibility(item.ownerName, '');
  const viewer = props.viewer;
  const mine = Boolean(viewer && (
    (viewer.id !== null && ownerId === viewer.id)
    || (viewer.name && ownerName.split('、').includes(viewer.name))
  ));
  /*
    轮到我审：**后端说了算**，界面不再叠条件（0914 Tony 第 9 条:
    「我的工作和项目管理 2 个模块轮到我审内容不一致」）。

    原来这里写的是 `props.writable && item.canDecide && inReview`。
    后端的 canDecide 就是「我名下有这条的活审批待办」——和首页那张卡同一条判据；
    界面又与了一个 inReview（`approval_status = 'pending'`），于是状态对不上的那批
    整条消失:SN26005 那条交付物是 status=approved / approval_status=rejected,
    待办却还活着,首页数 1 条、这里筛出 0 条。
    `writable` 也不该与:被派到审批的人本来就该能审,他在不在项目组是另一回事，
    真没权限后端 decide() 会拦。
  */
  const canDecide = item.canDecide === true;
  /*
    有没有人能审这条：后端按「节点挂了审批流 或 配了评审角色」算好传过来。
    两者都没有的，点提交必然撞在「既没有关联审批流，也没有配评审角色」上——
    那批在矩阵里三栏是 N/A，业务上本来就不用签字，只是要交个东西存档。
    0911 Tony 拍板：这种给「标记完成」，别再教人走进死路。
  */
  const noRoute = item.noApprovalRoute === true;
  const submittable = props.writable && hasFile && !inReview
    && SUBMITTABLE_STATUSES.has(status) && applicable;
  const canSubmit = submittable && !noRoute;
  const canComplete = submittable && noRoute;

  let chipValue = 'draft';
  let chipLabel = '待上传';
  if (!applicable) { chipValue = 'not_applicable'; chipLabel = '不适用'; }
  else if (cancelled) { chipValue = 'cancelled'; chipLabel = '已取消'; }
  else if (done) {
    chipValue = 'approved';
    chipLabel = status === 'completed' ? '已完成' : status === 'archived' ? '已归档' : '已批准';
  } else if (inReview) {
    chipValue = overdue ? 'overdue' : 'in_review';
    chipLabel = overdue ? '待审批·已逾期' : '待审批';
  } else if (rejected) { chipValue = 'rejected'; chipLabel = '被退回'; }
  else if (overdue) { chipValue = 'overdue'; chipLabel = hasFile ? '已逾期·未提交' : '已逾期·未上传'; }
  else if (hasFile) {
    chipValue = 'pending';
    chipLabel = noRoute ? '待确认完成' : '待提交';
  }

  return {
    hasFile, done, inReview, rejected, cancelled, overdue, applicable, mine,
    canDecide, canSubmit, canComplete, chipValue, chipLabel,
  };
}

const states = computed(() => new Map(props.items.map((item) => [idOf(item), stateOf(item)])));
function state(item: ModuleRecord): RowState {
  return states.value.get(idOf(item)) ?? stateOf(item);
}

/* ---------- 筛选 ---------- */

const query = ref('');
const quick = ref<QuickKey>('all');
const QUICK_KEYS = new Set<string>(['all', 'mine', 'decide', 'no-file', 'ready', 'in-review', 'rejected', 'overdue', 'done']);
watch(() => props.initialQuick, (value) => {
  if (value && QUICK_KEYS.has(value)) quick.value = value as QuickKey;
}, { immediate: true });
const phaseFilter = ref<string | null>(null);
/*
  Gate 提醒点过来时预选那一段（?phase=P05）。分组的 key 就是 phaseCode，
  所以参数直接就是 key，不用再映射一次。之后人自己点页签不会被覆盖：
  参数没变就不会再触发。
*/
watch(() => props.initialPhase, (value) => {
  if (value) phaseFilter.value = value;
}, { immediate: true });
/** 节点已判不适用的那批默认不占地方；要看的时候勾一下 */
const showNotApplicable = ref(false);

const QUICK_FILTERS: Array<{ key: QuickKey; label: string; hint: string; test: (state: RowState) => boolean }> = [
  { key: 'all', label: '全部', hint: '本项目所有交付物', test: () => true },
  { key: 'mine', label: '我负责的', hint: '责任人是我的', test: (s) => s.mine },
  { key: 'decide', label: '轮到我审', hint: '审批待办在我手上的', test: (s) => s.canDecide },
  { key: 'no-file', label: '缺文件', hint: '还没上传正文的', test: (s) => !s.hasFile && !s.done && !s.cancelled },
  { key: 'ready', label: '可提交', hint: '有文件、还没提交审批的', test: (s) => s.hasFile && !s.inReview && !s.done && !s.cancelled },
  { key: 'in-review', label: '审批中', hint: '已提交、等审批的', test: (s) => s.inReview },
  { key: 'rejected', label: '被退回', hint: '审批退回、要改了重交的', test: (s) => s.rejected },
  { key: 'overdue', label: '已逾期', hint: '过了要求完成日期还没批准的', test: (s) => s.overdue },
  { key: 'done', label: '已批准', hint: '审批通过 / 已完成 / 已归档', test: (s) => s.done },
];

/** 参与统计和显示的底数：不适用的默认剔掉 */
const baseItems = computed(() => props.items.filter((item) =>
  showNotApplicable.value || state(item).applicable));
const notApplicableCount = computed(() => props.items.filter((item) => !state(item).applicable).length);

const quickCounts = computed(() => Object.fromEntries(QUICK_FILTERS.map((filter) => [
  filter.key,
  baseItems.value.filter((item) => filter.test(state(item))).length,
])) as Record<QuickKey, number>);

/** 只显示有东西的快捷筛选，「全部」永远在；空的筛选按钮只会让人点了失望 */
const visibleQuickFilters = computed(() => QUICK_FILTERS.filter((filter) =>
  filter.key === 'all' || quickCounts.value[filter.key] > 0 || quick.value === filter.key));

function matchesQuery(item: ModuleRecord): boolean {
  const needle = query.value.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [item.title, item.code, item.ownerName, item.nodeName, item.parentNode, item.phase]
    .map((value) => formatResponsibility(value, '').toLowerCase())
    .join(' ');
  return haystack.includes(needle);
}

const activeQuick = computed(() => QUICK_FILTERS.find((filter) => filter.key === quick.value) ?? QUICK_FILTERS[0]);

const filteredItems = computed(() => baseItems.value.filter((item) =>
  activeQuick.value.test(state(item)) && matchesQuery(item)));

/* ---------- 分组 ---------- */

interface RowEntry { kind: 'row'; item: ModuleRecord; state: RowState }
interface SubheadEntry { kind: 'subhead'; label: string }
interface PhaseGroup {
  key: string;
  label: string;
  sort: number;
  total: number;
  done: number;
  withFile: number;
  overdue: number;
  entries: Array<RowEntry | SubheadEntry>;
}

function phaseKeyOf(item: ModuleRecord): string {
  return text(item.phaseCode) || text(item.phase) || '其他';
}

function phaseLabelOf(item: ModuleRecord): string {
  return text(item.phase) || '未挂阶段';
}

/** 阶段分组在筛选**之后**做：每组的数字说的是「当前筛选下这组还有几条」 */
const groups = computed<PhaseGroup[]>(() => {
  const map = new Map<string, PhaseGroup>();
  let order = 0;
  for (const item of filteredItems.value) {
    const key = phaseKeyOf(item);
    let group = map.get(key);
    if (!group) {
      const sort = typeof item.phaseSort === 'number' ? item.phaseSort : Number.MAX_SAFE_INTEGER - 1000 + order;
      group = { key, label: phaseLabelOf(item), sort, total: 0, done: 0, withFile: 0, overdue: 0, entries: [] };
      map.set(key, group);
      order += 1;
    }
    const rowState = state(item);
    group.total += 1;
    if (rowState.done) group.done += 1;
    if (rowState.hasFile) group.withFile += 1;
    if (rowState.overdue) group.overdue += 1;
    // 直接上级换了就插一行小标题——「P03-开发模具阶段」这种子阶段一眼分得开
    const parent = text(item.parentNode);
    const last = group.entries[group.entries.length - 1];
    const lastParent = last?.kind === 'subhead' ? last.label
      : last?.kind === 'row' ? text(last.item.parentNode) : '';
    if (parent && parent !== lastParent) group.entries.push({ kind: 'subhead', label: parent });
    group.entries.push({ kind: 'row', item, state: rowState });
  }
  return [...map.values()].sort((a, b) => a.sort - b.sort);
});

/** 阶段页签的底数按全部（不按快捷筛选），否则点了「缺文件」页签就全变 0 */
const phaseTabs = computed(() => {
  const map = new Map<string, { key: string; label: string; sort: number; total: number; done: number }>();
  let order = 0;
  for (const item of baseItems.value) {
    if (!matchesQuery(item)) continue;
    const key = phaseKeyOf(item);
    let tab = map.get(key);
    if (!tab) {
      const sort = typeof item.phaseSort === 'number' ? item.phaseSort : Number.MAX_SAFE_INTEGER - 1000 + order;
      tab = { key, label: key === '其他' ? '未挂阶段' : key, sort, total: 0, done: 0 };
      map.set(key, tab);
      order += 1;
    }
    tab.total += 1;
    if (state(item).done) tab.done += 1;
  }
  return [...map.values()].sort((a, b) => a.sort - b.sort);
});

/*
  预选的阶段这个项目里压根没有（链接里的参数写错、或者项目没那一段）——
  别把人晾在空白页上，退回全部。人手点页签选的一定存在，不会误伤。
*/
watch([phaseTabs, phaseFilter], () => {
  if (!phaseFilter.value || !phaseTabs.value.length) return;
  if (!phaseTabs.value.some((tab) => tab.key === phaseFilter.value)) phaseFilter.value = null;
}, { immediate: true });

const visibleGroups = computed(() => phaseFilter.value
  ? groups.value.filter((group) => group.key === phaseFilter.value)
  : groups.value);

const collapsed = ref<Set<string>>(new Set());
function isCollapsed(group: PhaseGroup): boolean {
  return collapsed.value.has(group.key);
}
function toggleGroup(group: PhaseGroup): void {
  const next = new Set(collapsed.value);
  if (next.has(group.key)) next.delete(group.key); else next.add(group.key);
  collapsed.value = next;
}

/** 全批完的阶段默认收起来——它们不再是待办；正在看的那条所在的组永远展开。
 *  勾「显示不适用」后底数变了，收起与否也要重算，否则那批永远在收着的组里看不见 */
watch(() => [props.items, showNotApplicable.value] as const, () => {
  const next = new Set<string>();
  for (const group of groups.value) {
    if (group.total > 0 && group.done === group.total) next.add(group.key);
  }
  collapsed.value = next;
}, { immediate: true });

watch(() => props.selectedId, (id) => {
  if (id === null || id === undefined) return;
  const target = props.items.find((item) => String(item.id) === String(id));
  if (!target) return;
  const key = phaseKeyOf(target);
  if (collapsed.value.has(key)) {
    const next = new Set(collapsed.value);
    next.delete(key);
    collapsed.value = next;
  }
}, { immediate: true });

function clearFilters(): void {
  query.value = '';
  quick.value = 'all';
  phaseFilter.value = null;
}

const filtersActive = computed(() => Boolean(query.value.trim()) || quick.value !== 'all' || phaseFilter.value !== null);

/* ---------- 行上动作 ---------- */

const busy = ref<Record<string, 'upload' | 'submit' | 'decide' | 'complete'>>({});
function setBusy(item: ModuleRecord, value: 'upload' | 'submit' | 'decide' | 'complete' | null): void {
  const next = { ...busy.value };
  if (value === null) delete next[idOf(item)]; else next[idOf(item)] = value;
  busy.value = next;
}
function busyOf(item: ModuleRecord): string {
  return busy.value[idOf(item)] ?? '';
}
const anyBusy = computed(() => Object.keys(busy.value).length > 0);

function downloadHref(item: ModuleRecord): string | null {
  if (!item.documentId || !item.documentVersionId) return null;
  return eqsApi.fileDownloadUrl(Number(item.documentId), Number(item.documentVersionId));
}

async function uploadForRow(item: ModuleRecord, event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  setBusy(item, 'upload');
  try {
    const form = new FormData();
    form.append('file', file);
    await eqsApi.attachDeliverableFile(props.projectId, item.id as string | number, form);
    notify('success', '已挂上文件', `${text(item.title)} ← ${file.name}`);
    emit('changed');
  } catch (caught) {
    notify('error', '上传失败', errorMessage(caught));
  } finally {
    // 清空 input：同一个文件再选一次也要能触发 change
    input.value = '';
    setBusy(item, null);
  }
}

/**
 * 一键提交：按审批流自动派发。
 * 派不到人（角色没配人 / 节点没挂审批流）时后端会说清楚是哪个角色；
 * 这时把详情打开，人可以在那里指定一个审批人——不让他在清单上干瞪眼。
 */
async function submitRow(item: ModuleRecord): Promise<void> {
  setBusy(item, 'submit');
  try {
    const saved = await eqsApi.submitProjectDeliverable(props.projectId, item.id as string | number);
    const waiting = saved?.waitingFor ? `，现在等 ${saved.waitingFor} 审批` : '';
    notify('success', '已提交审批', `${text(item.title)}${waiting}`);
    emit('changed');
  } catch (caught) {
    const message = errorMessage(caught);
    notify('error', '提交审批没有成功', message);
    if (/审批人|评审|角色|审批流/u.test(message)) emit('open', item);
  } finally {
    setBusy(item, null);
  }
}

/*
  没挂审批流的那一条，提交人自己选（0916 Tony:「有交付物但从不审批的，
  可以让提交人选择直接完成或者指定是否需要审批人」）。

  0911 只做了一半：这种行上只有「标记完成」。可「不需要签字」是模板的口径，
  不是每一次都成立——同一个活动，这个项目客户要看、下个项目不用，
  当事人比模板清楚。后端 submit() 一直收 approverUserId（0814 加的，
  给「审批流找不到人」兜底），只是这块界面没接上去。

  两条路都留在行上，不让人为了指定一个人跑去开详情弹窗。
*/
const approverPick = ref<{ item: ModuleRecord } | null>(null);
const pickedApproverId = ref<number | null>(null);
const pickedApproverName = ref('');

function askApprover(item: ModuleRecord): void {
  pickedApproverId.value = null;
  pickedApproverName.value = '';
  approverPick.value = { item };
}

/*
  认不出名字时**不能**只把文本发上去：后端 openDeliverableApproval 只认
  explicitApproverUserId，光给 approverName 会掉进「既没有关联审批流，也没有配评审角色」
  那条报错里——看着像提交失败，其实是没选到人。所以这里当场说清楚。
*/
function onApproverUnresolved(text: string): void {
  pickedApproverId.value = null;
  pickedApproverName.value = text;
}

async function submitToApprover(): Promise<void> {
  const current = approverPick.value;
  if (!current) return;
  const person = props.people.find((candidate) => candidate.id === pickedApproverId.value);
  if (!person) {
    notify('error', '还没选到人', pickedApproverName.value.trim()
      ? `通讯录里对不上「${pickedApproverName.value.trim()}」，从下拉里选一个。`
      : '从通讯录里选一个人，或者直接点「直接完成」。');
    return;
  }
  const name = person.displayName;
  setBusy(current.item, 'submit');
  try {
    await eqsApi.submitProjectDeliverable(
      props.projectId,
      current.item.id as string | number,
      person.id,
      name,
    );
    notify('success', '已提交审批', `${text(current.item.title)}，现在等 ${name} 审批`);
    approverPick.value = null;
    emit('changed');
  } catch (caught) {
    notify('error', '提交审批没有成功', errorMessage(caught));
  } finally {
    setBusy(current.item, null);
  }
}

/** 不用审批的交付物：传了文件就能直接收尾，不走审批链 */
async function completeRow(item: ModuleRecord): Promise<void> {
  setBusy(item, 'complete');
  try {
    await eqsApi.advanceProjectDeliverableStatus(props.projectId, item.id as string | number, 'completed');
    notify('success', '已标记完成', `${text(item.title)}（这项不需要审批）`);
    emit('changed');
  } catch (caught) {
    notify('error', '标记完成没有成功', errorMessage(caught));
  } finally {
    setBusy(item, null);
  }
}

const decision = ref<{ item: ModuleRecord; kind: 'approved' | 'rejected' } | null>(null);
const deciding = ref(false);

function askDecision(item: ModuleRecord, kind: 'approved' | 'rejected'): void {
  decision.value = { item, kind };
}

async function submitDecision(comment: string): Promise<void> {
  const current = decision.value;
  if (!current) return;
  deciding.value = true;
  setBusy(current.item, 'decide');
  try {
    await eqsApi.decideProjectDeliverable(
      props.projectId, current.item.id as string | number, current.kind, comment);
    notify('success', current.kind === 'approved' ? '已通过' : '已退回', text(current.item.title));
    decision.value = null;
    emit('changed');
  } catch (caught) {
    notify('error', '审批操作失败', errorMessage(caught));
  } finally {
    deciding.value = false;
    setBusy(current.item, null);
  }
}

/* ---------- 批量 ---------- */

const selection = ref<Set<string>>(new Set());
function isSelected(item: ModuleRecord): boolean {
  return selection.value.has(idOf(item));
}
function toggleSelect(item: ModuleRecord): void {
  const next = new Set(selection.value);
  const key = idOf(item);
  if (next.has(key)) next.delete(key); else next.add(key);
  selection.value = next;
}
function groupRows(group: PhaseGroup): ModuleRecord[] {
  return group.entries.filter((entry): entry is RowEntry => entry.kind === 'row').map((entry) => entry.item);
}
function groupAllSelected(group: PhaseGroup): boolean {
  const rows = groupRows(group);
  return rows.length > 0 && rows.every((item) => selection.value.has(idOf(item)));
}
function toggleGroupSelect(group: PhaseGroup): void {
  const next = new Set(selection.value);
  const rows = groupRows(group);
  if (groupAllSelected(group)) rows.forEach((item) => next.delete(idOf(item)));
  else rows.forEach((item) => next.add(idOf(item)));
  selection.value = next;
}
function clearSelection(): void {
  selection.value = new Set();
}
/** 清单一刷新（外层重新拉数据），勾选里不在清单上的条目就不该还留着 */
watch(() => props.items, (items) => {
  const present = new Set(items.map(idOf));
  const next = new Set([...selection.value].filter((key) => present.has(key)));
  if (next.size !== selection.value.size) selection.value = next;
});

const selectedItems = computed(() => props.items.filter((item) => selection.value.has(idOf(item))));
const selectedSubmittable = computed(() => selectedItems.value.filter((item) => state(item).canSubmit));

const bulkOwnerId = ref<number | null>(null);
const bulkDueDate = ref('');
const workCalendar = useWorkCalendar();
const bulkDueRestHint = computed(() => workCalendar.hint(bulkDueDate.value, '这个到期日'));
const bulkRunning = ref<{ label: string; done: number; total: number } | null>(null);

/**
 * 批量都是逐条调既有接口：每条各自留痕、各自校验，失败的单独点名。
 * 不另开批量端点——多一条写入口就多一份判据要同步。
 */
async function runBulk(
  label: string,
  targets: ModuleRecord[],
  action: (item: ModuleRecord) => Promise<unknown>,
): Promise<void> {
  if (!targets.length) return;
  bulkRunning.value = { label, done: 0, total: targets.length };
  const failed: string[] = [];
  for (const item of targets) {
    try {
      await action(item);
    } catch (caught) {
      failed.push(`${text(item.title)}：${errorMessage(caught)}`);
    }
    bulkRunning.value = { label, done: bulkRunning.value!.done + 1, total: targets.length };
  }
  bulkRunning.value = null;
  const okCount = targets.length - failed.length;
  if (failed.length) {
    notify('error', `${label}：${okCount} 条成功，${failed.length} 条没成`, failed.slice(0, 4).join('\n'));
  } else {
    notify('success', `${label}完成`, `${okCount} 条`);
  }
  clearSelection();
  emit('changed');
}

function bulkSubmit(): void {
  void runBulk('批量提交审批', selectedSubmittable.value, (item) =>
    eqsApi.submitProjectDeliverable(props.projectId, item.id as string | number));
}

function bulkAssignOwner(): void {
  const person = props.people.find((candidate) => candidate.id === bulkOwnerId.value);
  if (!person) {
    notify('error', '先选一个责任人', '从下拉建议里选人，输入的名字要能对上唯一的账号。');
    return;
  }
  void runBulk(`设责任人为 ${person.displayName}`, selectedItems.value, (item) =>
    eqsApi.updateProjectDeliverable(props.projectId, item.id as string | number, {
      ownerUserId: person.id,
      ownerName: person.displayName,
    }));
}

function bulkAssignDue(): void {
  const dueAt = deliverableDueAt(bulkDueDate.value);
  if (!dueAt) {
    notify('error', '先选一个日期', '要求完成日期留空就没什么可批量设的。');
    return;
  }
  void runBulk(`设完成日期为 ${bulkDueDate.value}`, selectedItems.value, (item) =>
    eqsApi.updateProjectDeliverable(props.projectId, item.id as string | number, { dueAt }));
}

function onOwnerUnresolved(typed: string): void {
  notify('error', '这个名字对不上账号', `「${typed}」在通讯录里找不到唯一的人，请从下拉建议里选。`);
}

/* ---------- 显示 ---------- */

/** 到期日按北京时间显示：库里存的是当天 23:59:59+08:00，按浏览器时区算会差一天 */
const DUE_FORMAT = new Intl.DateTimeFormat('zh-CN', {
  timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
});
function dueLabel(item: ModuleRecord): string {
  const raw = text(item.dueAt);
  if (!raw) return '无期限';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return DUE_FORMAT.format(date).replaceAll('/', '-');
}

function ownerLabel(item: ModuleRecord): string {
  return formatResponsibility(item.ownerName, '');
}

function approvalHint(item: ModuleRecord, rowState: RowState): string {
  if (rowState.canDecide) return '轮到你审批';
  if (rowState.inReview) return item.waitingFor ? `等 ${text(item.waitingFor)} 审批` : '审批中（没查到该谁审）';
  if (rowState.rejected) return '被退回，改完可重新提交';
  return '';
}
</script>

<template>
  <section class="dlv-board" :class="{ 'is-readonly': !writable }">
    <div class="dlv-toolbar">
      <input
        v-model="query"
        class="dlv-search"
        type="search"
        placeholder="搜交付物 / 节点 / 责任人"
        aria-label="搜索交付物"
      >
      <div class="dlv-quick" role="group" aria-label="快捷筛选">
        <button
          v-for="filter in visibleQuickFilters"
          :key="filter.key"
          type="button"
          class="dlv-chip"
          :class="{ 'is-on': quick === filter.key, [`is-${filter.key}`]: true }"
          :title="filter.hint"
          @click="quick = quick === filter.key ? 'all' : filter.key"
        >{{ filter.label }}<b>{{ quickCounts[filter.key] }}</b></button>
      </div>
      <label v-if="notApplicableCount" class="dlv-na-toggle">
        <input v-model="showNotApplicable" type="checkbox">
        显示不适用的 {{ notApplicableCount }} 条
      </label>
    </div>

    <div v-if="phaseTabs.length > 1" class="dlv-phases" role="tablist" aria-label="按阶段">
      <button
        type="button"
        class="dlv-phase"
        :class="{ 'is-on': phaseFilter === null }"
        @click="phaseFilter = null"
      >全部阶段</button>
      <button
        v-for="tab in phaseTabs"
        :key="tab.key"
        type="button"
        class="dlv-phase"
        :class="{ 'is-on': phaseFilter === tab.key, 'is-done': tab.done === tab.total }"
        :title="`已批准 ${tab.done} / ${tab.total}`"
        @click="phaseFilter = phaseFilter === tab.key ? null : tab.key"
      >{{ tab.label }}<small>{{ tab.done }}/{{ tab.total }}</small></button>
    </div>

    <div v-if="writable && selection.size" class="dlv-bulk">
      <b>已选 {{ selection.size }} 条</b>
      <template v-if="bulkRunning">
        <span class="dlv-bulk-progress">{{ bulkRunning.label }}… {{ bulkRunning.done }} / {{ bulkRunning.total }}</span>
      </template>
      <template v-else>
        <button
          class="inline-button"
          type="button"
          :disabled="!selectedSubmittable.length"
          :title="selectedSubmittable.length ? '按审批流自动派发' : '选中的条目里没有「有文件且还没提交」的'"
          @click="bulkSubmit"
        >提交审批（{{ selectedSubmittable.length }} 条可提）</button>
        <span class="dlv-bulk-field">
          <PersonPicker
            :people="people"
            :model-value="bulkOwnerId"
            label="批量设责任人"
            list-id="deliverable-bulk-owner"
            placeholder="输入姓名设责任人"
            @update:model-value="bulkOwnerId = $event"
            @unresolved="onOwnerUnresolved"
          />
          <datalist id="deliverable-bulk-owner">
            <option v-for="person in people" :key="person.id" :value="personLabel(person)" />
          </datalist>
          <button class="inline-button" type="button" @click="bulkAssignOwner">设责任人</button>
        </span>
        <span class="dlv-bulk-field">
          <input v-model="bulkDueDate" type="date" aria-label="批量设要求完成日期">
          <button class="inline-button" type="button" @click="bulkAssignDue">设完成日期</button>
          <!-- 1006 工作日历：到期日落在周末 / 假日上提示一句 -->
          <small v-if="bulkDueRestHint" class="dlv-rest-hint">{{ bulkDueRestHint }}</small>
        </span>
        <button class="inline-button" type="button" @click="clearSelection">取消选择</button>
      </template>
    </div>

    <div v-if="!filteredItems.length" class="dlv-empty">
      <template v-if="filtersActive">
        <b>当前筛选下没有交付物</b>
        <span>「{{ activeQuick.label }}」{{ phaseFilter ? ` · ${phaseFilter}` : '' }}{{ query.trim() ? ` · 搜「${query.trim()}」` : '' }} 一条都没对上。</span>
        <button class="inline-button" type="button" @click="clearFilters">清除筛选</button>
      </template>
      <template v-else-if="notApplicableCount && !showNotApplicable">
        <b>这些交付物所在的节点都已判为不适用</b>
        <span>勾上「显示不适用」可以看到它们。</span>
      </template>
    </div>

    <section
      v-for="group in visibleGroups"
      :key="group.key"
      class="dlv-group"
      :class="{ 'is-collapsed': isCollapsed(group) }"
    >
      <header class="dlv-group-head">
        <label v-if="writable" class="dlv-check" :title="groupAllSelected(group) ? '取消勾选本阶段' : '勾选本阶段全部'">
          <input type="checkbox" :checked="groupAllSelected(group)" @change="toggleGroupSelect(group)">
        </label>
        <button type="button" class="dlv-group-toggle" :aria-expanded="!isCollapsed(group)" @click="toggleGroup(group)">
          <i aria-hidden="true">{{ isCollapsed(group) ? '▸' : '▾' }}</i>
          <b>{{ group.label }}</b>
          <span class="dlv-group-stats">
            已批准 {{ group.done }} / {{ group.total }}
            <em v-if="group.withFile < group.total">· 缺文件 {{ group.total - group.withFile }}</em>
            <em v-if="group.overdue" class="is-risk">· 逾期 {{ group.overdue }}</em>
          </span>
          <span class="dlv-progress" aria-hidden="true">
            <i :style="{ width: `${group.total ? Math.round((group.done / group.total) * 100) : 0}%` }"></i>
          </span>
        </button>
      </header>

      <template v-if="!isCollapsed(group)">
        <template v-for="(entry, index) in group.entries" :key="entry.kind === 'row' ? `row-${entry.item.id}` : `sub-${group.key}-${index}`">
          <div v-if="entry.kind === 'subhead'" class="dlv-subhead">{{ entry.label }}</div>
          <div
            v-else
            class="record-row dlv-row"
            :class="{
              'is-active': selectedId !== null && selectedId !== undefined && String(selectedId) === String(entry.item.id),
              'is-selected': isSelected(entry.item),
              'is-done': entry.state.done,
              'is-na': !entry.state.applicable,
              'is-mine': entry.state.mine,
            }"
          >
            <label v-if="writable" class="dlv-check">
              <input type="checkbox" :checked="isSelected(entry.item)" :aria-label="`勾选 ${formatCell(entry.item.title)}`" @change="toggleSelect(entry.item)">
            </label>
            <StatusChip :value="entry.state.chipValue" :label="entry.state.chipLabel" />
            <button class="record-open dlv-main" type="button" :title="'打开详情：改字段、看审批链和改动史'" @click="emit('open', entry.item)">
              <b>
                {{ formatCell(entry.item.title) }}
                <small v-if="entry.item.code" class="dlv-code">{{ entry.item.code }}</small>
                <small v-if="entry.state.mine" class="dlv-mine-tag">我负责</small>
              </b>
              <small class="dlv-meta">
                <span :class="{ 'is-missing': !ownerLabel(entry.item) }">{{ ownerLabel(entry.item) || '未指定责任人' }}</span>
                <span :class="{ 'is-risk': entry.state.overdue }">{{ dueLabel(entry.item) }}</span>
                <span v-if="entry.item.documentFileName" class="dlv-file">{{ entry.item.documentFileName }}</span>
                <span v-else-if="entry.state.hasFile" class="dlv-file">已挂文件</span>
                <span v-if="approvalHint(entry.item, entry.state)" class="dlv-approval" :class="{ 'is-turn': entry.state.canDecide }">{{ approvalHint(entry.item, entry.state) }}</span>
              </small>
            </button>
            <div class="dlv-actions">
              <a
                v-if="downloadHref(entry.item)"
                class="inline-button"
                :href="downloadHref(entry.item)!"
                target="_blank"
                rel="noopener"
                :title="`下载 ${entry.item.documentFileName || '交付物正文'}`"
              >下载</a>
              <template v-if="entry.state.canComplete">
                <button
                  class="inline-button dlv-complete"
                  type="button"
                  :disabled="Boolean(busyOf(entry.item))"
                  title="这项在审批矩阵里不需要会签/审核/批准，传了文件即可收尾"
                  @click="completeRow(entry.item)"
                >直接完成</button>
                <button
                  class="inline-button"
                  type="button"
                  :disabled="Boolean(busyOf(entry.item))"
                  title="这项没配审批流；如果这次要人签字，指定一个审批人提交"
                  @click="askApprover(entry.item)"
                >指定审批人</button>
              </template>
              <template v-if="entry.state.canDecide">
                <button class="inline-button dlv-approve" type="button" :disabled="Boolean(busyOf(entry.item))" @click="askDecision(entry.item, 'approved')">通过</button>
                <button class="inline-button inline-button-danger" type="button" :disabled="Boolean(busyOf(entry.item))" @click="askDecision(entry.item, 'rejected')">退回</button>
              </template>
              <button
                v-if="entry.state.canSubmit"
                class="inline-button dlv-submit"
                type="button"
                :disabled="Boolean(busyOf(entry.item)) || anyBusy && busyOf(entry.item) !== 'submit'"
                title="按审批流自动派发审批人"
                @click="submitRow(entry.item)"
              >{{ busyOf(entry.item) === 'submit' ? '提交中…' : entry.state.rejected ? '重新提交' : '提交审批' }}</button>
              <label
                v-if="writable && !entry.state.done && !entry.state.cancelled"
                class="record-upload"
                :class="{ 'has-file': entry.state.hasFile, busy: busyOf(entry.item) === 'upload' }"
                :title="entry.state.hasFile ? '已挂文件；再传一个会作为新版本' : '直接选文件上传并挂给这个交付物'"
              >
                {{ busyOf(entry.item) === 'upload' ? '上传中…' : entry.state.hasFile ? '传新版' : '＋ 传文件' }}
                <input type="file" :disabled="Boolean(busyOf(entry.item))" @change="uploadForRow(entry.item, $event)">
              </label>
              <button class="inline-button dlv-detail" type="button" @click="emit('open', entry.item)">详情</button>
            </div>
          </div>
        </template>
      </template>
    </section>

    <div
      v-if="approverPick"
      class="dlv-pick-mask"
      @click.self="!busyOf(approverPick.item) && (approverPick = null)"
    >
      <section class="dlv-pick" role="dialog" aria-modal="true" aria-labelledby="dlv-pick-title">
        <header>
          <div>
            <span>SUBMIT FOR APPROVAL</span>
            <h3 id="dlv-pick-title">指定一个人来审批</h3>
            <p>{{ formatCell(approverPick.item.title) }}</p>
          </div>
          <button
            class="inline-button"
            type="button"
            :disabled="Boolean(busyOf(approverPick.item))"
            @click="approverPick = null"
          >关闭</button>
        </header>
        <form @submit.prevent="submitToApprover">
          <p class="dlv-pick-note">
            这项在审批矩阵里没有会签／审核／批准，所以默认可以<b>直接完成</b>。
            如果这次确实要人签字，在这里指定审批人——只影响这一条，不改模板。
          </p>
          <PersonPicker
            :people="people"
            :model-value="pickedApproverId"
            label="审批人"
            list-id="dlv-pick-approver"
            placeholder="输入姓名选人"
            @update:model-value="pickedApproverId = $event"
            @unresolved="onApproverUnresolved"
          />
          <datalist id="dlv-pick-approver">
            <option v-for="person in people" :key="person.id" :value="personLabel(person)" />
          </datalist>
          <footer>
            <button
              class="button button-primary"
              type="submit"
              :disabled="Boolean(busyOf(approverPick.item))"
            >{{ busyOf(approverPick.item) === 'submit' ? '提交中…' : '提交给他审批' }}</button>
            <button
              class="button"
              type="button"
              :disabled="Boolean(busyOf(approverPick.item))"
              @click="approverPick = null"
            >取消</button>
          </footer>
        </form>
      </section>
    </div>

    <ApprovalDecisionDialog
      :decision="decision?.kind ?? null"
      :subject="decision ? formatCell(decision.item.title) : ''"
      :saving="deciding"
      @close="decision = null"
      @submit="submitDecision"
    />
  </section>
</template>

<style scoped>
.dlv-board { display: grid; gap: 10px; }

.dlv-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; }
.dlv-search { flex: 0 1 260px; min-height: 34px; padding: 0 10px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); color: var(--ink); font-size: 13px; }
.dlv-search:focus { outline: 2px solid var(--blue); outline-offset: 1px; }
.dlv-quick { display: flex; flex-wrap: wrap; gap: 6px; }
.dlv-chip { display: inline-flex; align-items: center; gap: 6px; min-height: 30px; padding: 0 10px; border: 1px solid var(--line-strong); border-radius: 999px; background: var(--white); color: var(--ink); font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; }
.dlv-chip b { min-width: 18px; padding: 1px 6px; border-radius: 999px; background: rgba(100,116,139,.12); color: var(--muted); font-size: 11px; text-align: center; }
.dlv-chip:hover { border-color: var(--blue); }
.dlv-chip.is-on { border-color: var(--blue); background: rgba(0,117,174,.1); color: var(--blue); }
.dlv-chip.is-on b { background: var(--blue); color: #fff; }
.dlv-chip.is-decide b, .dlv-chip.is-overdue b, .dlv-chip.is-rejected b { background: rgba(196,63,56,.12); color: var(--risk); }
.dlv-chip.is-mine b { background: rgba(0,117,174,.12); color: var(--blue); }
.dlv-na-toggle { display: inline-flex; align-items: center; gap: 6px; margin-left: auto; color: var(--muted); font-size: 12px; cursor: pointer; }

.dlv-phases { display: flex; flex-wrap: wrap; gap: 4px; padding-bottom: 8px; border-bottom: 1px solid var(--line); }
.dlv-phase { display: inline-flex; align-items: baseline; gap: 6px; min-height: 30px; padding: 0 10px; border: 1px solid transparent; border-radius: var(--radius-s); background: transparent; color: var(--ink); font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; }
.dlv-phase small { color: var(--muted); font-size: 11px; font-weight: 600; }
.dlv-phase:hover { background: rgba(0,117,174,.07); }
.dlv-phase.is-on { border-color: var(--blue); background: rgba(0,117,174,.1); color: var(--blue); }
.dlv-phase.is-done small { color: var(--green); }

.dlv-bulk { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; padding: 8px 12px; border: 1px solid rgba(0,117,174,.35); border-radius: var(--radius-s); background: rgba(0,117,174,.06); font-size: 12px; }
.dlv-bulk > b { font-size: 13px; }
.dlv-bulk-field { display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.dlv-rest-hint { flex-basis: 100%; font-size: 12px; color: #b06a12; }
.dlv-bulk-field input[type="date"] { min-height: 30px; padding: 0 8px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); font: inherit; font-size: 12px; }
.dlv-bulk-field :deep(input[type="text"]), .dlv-bulk-field :deep(input:not([type])) { min-height: 30px; min-width: 180px; padding: 0 8px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); font: inherit; font-size: 12px; }
.dlv-bulk-progress { color: var(--blue); font-weight: 700; }

.dlv-empty { display: grid; gap: 6px; justify-items: start; padding: 18px 14px; border: 1px dashed var(--line-strong); border-radius: var(--radius-s); color: var(--muted); font-size: 13px; }
.dlv-empty b { color: var(--ink); }

.dlv-group { border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--white); overflow: hidden; }
.dlv-group + .dlv-group { margin-top: 2px; }
.dlv-group-head { display: flex; align-items: center; gap: 4px; padding: 0 10px 0 6px; background: #f5f8fc; }
.dlv-group-toggle { flex: 1; min-width: 0; display: grid; grid-template-columns: 14px auto 1fr minmax(80px, 160px); align-items: center; gap: 10px; min-height: 42px; padding: 0 4px; border: 0; background: transparent; color: var(--ink); font: inherit; text-align: left; cursor: pointer; }
.dlv-group-toggle > i { color: var(--muted); font-style: normal; font-size: 12px; }
.dlv-group-toggle > b { font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dlv-group-stats { color: var(--muted); font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dlv-group-stats em { font-style: normal; }
.dlv-group-stats em.is-risk { color: var(--risk); font-weight: 700; }
.dlv-progress { position: relative; height: 6px; border-radius: 999px; background: rgba(100,116,139,.15); overflow: hidden; }
.dlv-progress i { position: absolute; inset: 0 auto 0 0; background: var(--green); }
.dlv-group.is-collapsed .dlv-group-head { background: transparent; }

.dlv-subhead { padding: 6px 12px 4px 46px; border-top: 1px solid var(--line); color: var(--muted); font-size: 11px; font-weight: 700; letter-spacing: .02em; background: #fbfcfe; }
.is-readonly .dlv-subhead { padding-left: 14px; }

.dlv-row { display: grid; grid-template-columns: 32px 100px minmax(0, 1fr) auto; align-items: center; gap: 8px; padding: 0 10px 0 6px; border-top: 1px solid var(--line); }
.is-readonly .dlv-row { grid-template-columns: 100px minmax(0, 1fr) auto; padding-left: 10px; }
.dlv-row.is-selected { background: rgba(0,117,174,.04); }
.dlv-row.is-done { opacity: .78; }
.dlv-row.is-na { opacity: .55; }
.dlv-check { display: inline-flex; align-items: center; justify-content: center; min-width: 28px; min-height: 28px; cursor: pointer; }
.dlv-check input { width: 15px; height: 15px; margin: 0; cursor: pointer; }
/* 覆盖全局 .record-open 的三列网格：这里标题区自己排 */
.dlv-row .dlv-main { display: grid; grid-template-columns: 1fr; gap: 3px; min-height: 54px; padding: 8px 0; border: 0; }
.dlv-main b { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px; font-size: 13px; }
.dlv-code { color: var(--muted); font-size: 11px; font-weight: 600; }
.dlv-mine-tag { padding: 0 6px; border-radius: 999px; background: rgba(0,117,174,.1); color: var(--blue); font-size: 10px; font-weight: 800; }
.dlv-meta { display: flex; flex-wrap: wrap; gap: 4px 12px; color: var(--muted); font-size: 12px; }
.dlv-meta .is-missing { color: var(--warning); font-weight: 700; }
.dlv-meta .is-risk { color: var(--risk); font-weight: 700; }
.dlv-file { max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dlv-approval { color: var(--ink); }
.dlv-approval.is-turn { color: var(--risk); font-weight: 800; }

.dlv-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; gap: 6px; padding: 6px 0; }
.dlv-actions .record-upload { min-height: 28px; padding: 0 9px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); }
.dlv-actions .dlv-submit { border-color: var(--blue); background: var(--blue); color: #fff; }
.dlv-actions .dlv-submit:hover { background: #005f8c; }
.dlv-actions .dlv-submit:disabled { background: var(--paper); color: var(--muted); border-color: var(--line); }
.dlv-actions .dlv-approve { border-color: var(--green); color: var(--green); }
.dlv-actions .dlv-approve:hover { background: rgba(38,122,99,.08); }
/*
  指定审批人的小弹窗（0916）。**不能写在 .dlv-actions 下面**——
  第一版就是这么挂进去的，`position: fixed` 被更具体的祖先选择器一并作用域化后
  整块退回 static，弹窗跟着行渲染在页面 1132px 处，屏幕上什么都看不到。
  单测全绿（jsdom 不算布局），是真浏览器里量 getBoundingClientRect 才抓到的。
*/
.dlv-pick-mask { position: fixed; inset: 0; z-index: 60; display: flex; align-items: flex-start; justify-content: center; padding: 6vh 16px; background: rgba(15,23,36,.45); overflow-y: auto; }
.dlv-pick { width: min(520px, 100%); background: var(--white); border: 1px solid var(--line); box-shadow: 0 18px 48px rgba(15,23,36,.22); }
.dlv-pick > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--line); }
.dlv-pick header span { color: var(--muted); font: 700 12px var(--display); letter-spacing: .08em; }
.dlv-pick header h3 { margin: 3px 0 0; font-size: 18px; }
.dlv-pick header p { margin: 4px 0 0; max-width: 42ch; font-size: 13px; line-height: 1.6; }
.dlv-pick form { display: grid; gap: 12px; padding: 16px 20px; }
.dlv-pick-note { margin: 0; color: var(--muted); font-size: 12.5px; line-height: 1.7; }
.dlv-pick-note b { color: var(--ink); }
.dlv-pick footer { display: flex; gap: 8px; }

/* 「直接完成」是收尾动作，用和「通过」同一族的绿，但不抢审批按钮的位置 */
.dlv-actions .dlv-complete { border-color: var(--green); color: var(--green); }
.dlv-actions .dlv-complete:hover { background: rgba(38,122,99,.08); }
.dlv-actions .dlv-detail { color: var(--muted); }

@media (max-width: 900px) {
  .dlv-row { grid-template-columns: 32px minmax(0, 1fr); }
  .is-readonly .dlv-row { grid-template-columns: minmax(0, 1fr); }
  .dlv-row .status-chip { grid-column: 2; }
  .is-readonly .dlv-row .status-chip { grid-column: 1; }
  .dlv-actions { grid-column: 1 / -1; justify-content: flex-start; padding-left: 32px; }
}
</style>
